import React, { useEffect, useMemo, useState } from 'react';
import { researchToolsApi } from '../api';
import type { ExperimentDetail, SurveyAnalytics, SurveyDetail } from '../types';
import { AnalyticsModal } from './AnalyticsModal';
import { SurveyHubHero } from './SurveyHubHero';
import '../survey-hub.css';

interface Props {
  onBack: () => void;
  onOpenCreate: () => void;
  onSelectSurveyToPublish: (survey: SurveyDetail) => void;
}

type RegistryItem =
  | { kind: 'survey'; data: SurveyDetail }
  | { kind: 'experiment'; data: ExperimentDetail };

function statusLabel(status: string): string {
  if (status === 'APPROVED') return 'Утверждён';
  if (status === 'MODERATION') return 'На модерации';
  if (status === 'DRAFT') return 'Черновик';
  return status;
}

function experimentTypeLabel(type: string): string {
  if (type === 'ASSOCIATIVE') return 'Ассоциативный';
  if (type === 'STROOP') return 'Stroop';
  if (type === 'REACTION') return 'Реакция';
  return type;
}

/** Unified surveys + experiments registry — template dashboard(). */
export const MySurveysDashboard: React.FC<Props> = ({
  onBack,
  onOpenCreate,
  onSelectSurveyToPublish,
}) => {
  const [items, setItems] = useState<RegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [activeAnalytics, setActiveAnalytics] = useState<SurveyAnalytics | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      researchToolsApi.getSurveys('ALL', ''),
      researchToolsApi.getExperiments('ALL'),
    ])
      .then(([surveys, experiments]) => {
        const merged: RegistryItem[] = [
          ...surveys.map((data) => ({ kind: 'survey' as const, data })),
          ...experiments.map((data) => ({ kind: 'experiment' as const, data })),
        ].sort(
          (a, b) =>
            new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime()
        );
        setItems(merged);
        const firstSurvey = surveys[0];
        setSelectedSurveyId(firstSurvey?.id ?? null);
      })
      .finally(() => setLoading(false));
  }, []);

  const selectedSurvey = useMemo(
    () =>
      items.find((item) => item.kind === 'survey' && item.data.id === selectedSurveyId)?.data as
        | SurveyDetail
        | undefined,
    [items, selectedSurveyId]
  );

  const handleCopyLink = (path: string) => {
    navigator.clipboard.writeText(`${window.location.origin}${path}`);
  };

  const handleOpenAnalytics = async (surveyId: string) => {
    try {
      setActiveAnalytics(await researchToolsApi.getSurveyAnalytics(surveyId));
    } catch {
      alert('Ошибка при загрузке аналитики');
    }
  };

  const handleExportCsv = () => {
    if (!selectedSurvey) {
      alert('Выберите опрос для экспорта CSV');
      return;
    }
    window.open(researchToolsApi.getExportSurveyCsvUrl(selectedSurvey.id), '_blank');
  };

  return (
    <div className="survey-hub-content">
      <SurveyHubHero
        title="Опросы и эксперименты"
        description="Единый реестр опросов и экспериментов: ссылки, статусы, аналитика и выгрузка. Данные разделены по автору, проекту и типу."
      />

      <section className="survey-hub-panel">
        <header>
          <div className="grow">
            <h3>Опросы и эксперименты</h3>
            <p>Черновик → модерация → утверждён</p>
          </div>
          <div className="survey-hub-actions" style={{ margin: 0 }}>
            <button type="button" className="survey-hub-btn" onClick={onBack}>
              ← Назад
            </button>
            <button type="button" className="survey-hub-btn primary" onClick={onOpenCreate}>
              + Создать опрос
            </button>
            <button
              type="button"
              className="survey-hub-btn"
              onClick={handleExportCsv}
              disabled={!selectedSurvey}
              title={selectedSurvey ? `Экспорт: ${selectedSurvey.title}` : 'Выберите опрос в списке'}
            >
              Экспорт CSV
            </button>
          </div>
        </header>

        {loading ? <div className="survey-hub-note">Загрузка…</div> : null}
        {!loading && !items.length ? (
          <div className="survey-hub-note">Пока нет созданных опросов или экспериментов.</div>
        ) : null}

        {items.map((entry) => {
          if (entry.kind === 'survey') {
            const s = entry.data;
            const isSelected = selectedSurveyId === s.id;
            return (
              <div
                key={`survey-${s.id}`}
                className={`survey-hub-survey-row${isSelected ? ' is-selected' : ''}`}
                onClick={() => setSelectedSurveyId(s.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedSurveyId(s.id);
                }}
              >
                <div>
                  <h4>{s.title}</h4>
                  <small>
                    {s.id} · {s.audience} · {s.language}
                  </small>
                </div>
                <div className="survey-hub-actions" style={{ margin: 0, gap: 6 }}>
                  <span className="survey-hub-chip">Опрос</span>
                  <span className={`survey-hub-chip${s.status === 'APPROVED' ? ' ok' : ''}`}>
                    {statusLabel(s.status)}
                  </span>
                </div>
                <div>
                  <b>{s.responses_count ?? 0}</b>
                  <small> ответов</small>
                </div>
                <div
                  className="survey-hub-actions"
                  style={{ margin: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.status === 'APPROVED' && s.guest_token ? (
                    <button
                      type="button"
                      className="survey-hub-btn"
                      onClick={() => handleCopyLink(`/guest/survey/${s.guest_token}`)}
                    >
                      Копировать ссылку
                    </button>
                  ) : null}
                  <button type="button" className="survey-hub-btn" onClick={() => handleOpenAnalytics(s.id)}>
                    Аналитика
                  </button>
                  <button type="button" className="survey-hub-btn" onClick={() => onSelectSurveyToPublish(s)}>
                    Ссылка / аналитика
                  </button>
                  <button
                    type="button"
                    className="survey-hub-btn"
                    onClick={() =>
                      window.open(researchToolsApi.getExportSurveyCsvUrl(s.id), '_blank')
                    }
                  >
                    CSV
                  </button>
                </div>
              </div>
            );
          }

          const exp = entry.data;
          return (
            <div key={`experiment-${exp.id}`} className="survey-hub-survey-row">
              <div>
                <h4>{exp.title}</h4>
                <small>
                  {exp.id} · {experimentTypeLabel(exp.experiment_type)} · {exp.stimuli_questions.length}{' '}
                  стимулов
                </small>
              </div>
              <div className="survey-hub-actions" style={{ margin: 0, gap: 6 }}>
                <span className="survey-hub-chip">Эксперимент</span>
                <span className={`survey-hub-chip${exp.status === 'APPROVED' ? ' ok' : ''}`}>
                  {statusLabel(exp.status)}
                </span>
              </div>
              <div>
                <b>{exp.responses_count ?? 0}</b>
                <small> ответов</small>
              </div>
              <div className="survey-hub-actions" style={{ margin: 0 }}>
                {exp.status === 'APPROVED' && exp.guest_token ? (
                  <button
                    type="button"
                    className="survey-hub-btn"
                    onClick={() => handleCopyLink(`/guest/experiment/${exp.guest_token}`)}
                  >
                    Копировать ссылку
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>

      {activeAnalytics ? (
        <AnalyticsModal analytics={activeAnalytics} onClose={() => setActiveAnalytics(null)} />
      ) : null}
    </div>
  );
};
